import { useCallback, useMemo, useState } from 'react';
import { failHard } from '@resource-kit/core';
import type { Condition, FilterPreset, OperatorId } from '@resource-kit/model';
import { droppedFilterFields, type FilterWhitelist } from '../list/whitelist';
import { serializeConditionsToParams } from '../query/serialize';
import { mergeConditionRows } from '../query/domain';
import type { ListStatus } from '../list/types';
import { applyClientQuery } from '../list/clientPagination';
import { resolveListCapabilities, type ListCapabilities } from '../list/listCapabilities';
import type { ListController, ListPagination } from './listController';

const DEFAULT_LIMIT = 10;

export interface LocalListOptions<T> {
  /** The rows. The caller owns them; this hook fetches nothing. */
  rows: readonly T[];
  /**
   * Fields free-text search may match. REQUIRED (design §6.6): derived from
   * every field, local search silently reaches columns that must not be
   * searched. The view declares the set.
   */
  searchFields: readonly string[];
  /** Per-capability opt-in; anything omitted takes the source default (§6.6). */
  capabilities?: ListCapabilities;
  /** Initial sort, as `<field>:<asc|desc>`. */
  defaultSort?: string;
  /** Initial page size. */
  limit?: number;
  /**
   * Optional filter whitelist for the embedded surface. Present ⇒ a condition
   * outside it is reported through `droppedFilters` so the view can warn,
   * instead of the operator reading an unfiltered list as a filtered one.
   */
  filterable?: FilterWhitelist;
  /** Row selection wiring, if the embedding view offers it. */
  selectable?: boolean;
  /** Whether the embedding view declared row actions. */
  hasRowActions?: boolean;
  /** The caller's own load/error state, if its rows arrive asynchronously. */
  status?: ListStatus;
  error?: string | null;
  onRetry?: () => void;
}

const EMPTY_PRESETS = undefined;

/**
 * The caller-owned list source (#1803, design §6.6).
 *
 * The rows arrive as a prop and every bit of query state lives in this hook's
 * React state. **It never writes the URL — under any operation.** That is the
 * whole reason `local` is a distinct source: a tab-embedded list is a fragment
 * of somebody else's page, and writing its needle into the URL would pollute
 * share links and browser history and fight the parent list's own `search`.
 *
 * It declares no presets either (`presets` is `undefined`, `activePresetId`
 * is always `null`), so there is no preset state to persist and no "today"
 * to read: the hook reads no kit context.
 */
export function useLocalList<T>(options: LocalListOptions<T>): ListController<T> {
  const {
    rows: sourceRows,
    searchFields,
    capabilities: requestedCapabilities,
    defaultSort,
    filterable,
    selectable,
    hasRowActions,
    status = 'ready',
    error = null,
    onRetry,
  } = options;
  const initialLimit = options.limit ?? DEFAULT_LIMIT;

  // ── State: React, never the URL ────────────────────────────────────────
  const [page, setPageState] = useState(1);
  const [limit, setLimitState] = useState(initialLimit);
  const [search, setSearchState] = useState('');
  const [conditions, setConditionsState] = useState<Condition[]>([]);
  const [sort, setSortState] = useState(defaultSort ?? 'created_at:desc');

  const sortField = sort.split(':')[0];
  const sortOrder: 'asc' | 'desc' = sort.split(':')[1] === 'asc' ? 'asc' : 'desc';

  const capabilities = useMemo(
    () =>
      resolveListCapabilities('local', requestedCapabilities, {
        filterable,
        selectable,
        hasRowActions,
      }),
    [requestedCapabilities, filterable, selectable, hasRowActions],
  );

  const setPage = useCallback((next: number) => setPageState(Math.max(1, next)), []);

  // Page-size, search and filter changes all reset to page 1 — the same
  // pagination contract the URL-owned sources honour.
  const setLimit = useCallback((next: number) => {
    setLimitState(next);
    setPageState(1);
  }, []);

  const setSearch = useCallback((next: string) => {
    setSearchState(next);
    setPageState(1);
  }, []);

  const setSort = useCallback((field: string) => {
    setSortState((prev) => {
      const [prevField, prevOrder] = prev.split(':');
      return prevField === field
        ? `${field}:${prevOrder === 'asc' ? 'desc' : 'asc'}`
        : `${field}:asc`;
    });
  }, []);

  const setConditions = useCallback((next: Condition[]) => {
    setConditionsState(next);
    setPageState(1);
  }, []);

  const setFieldCondition = useCallback(
    (field: string, op: OperatorId, value: string | [string, string] | null) => {
      setConditionsState((prev) => {
        const next = prev.filter((c) => c.field !== field);
        if (value !== null) next.push({ id: `${field}::${op}`, field, op, value });
        return next;
      });
      setPageState(1);
    },
    [],
  );

  // `togglePreset` is part of the `ListController` contract, but this source
  // declares no presets, so a view never offers one to toggle (#2780).
  const togglePreset = useCallback((preset: FilterPreset): void => {
    failHard(`the local list source declares no presets; cannot toggle "${preset.id}"`);
  }, []);

  const clearFilters = useCallback(() => {
    setSearchState('');
    setConditionsState([]);
    setPageState(1);
  }, []);

  const isFiltered = search !== '' || conditions.length > 0;

  // Same-field collapses, reported through exactly the function the wire
  // layer uses — so what the view announces is what actually happened.
  const mergeNotices = useMemo(() => mergeConditionRows(conditions).notices, [conditions]);

  const extraParams = useMemo(
    () => (conditions.length > 0 ? serializeConditionsToParams(conditions, {}) : undefined),
    [conditions],
  );

  const droppedFilters = useMemo(
    () => droppedFilterFields(extraParams, filterable),
    [extraParams, filterable],
  );

  const result = useMemo(
    () =>
      applyClientQuery(sourceRows, {
        search,
        conditions,
        sortField,
        sortOrder,
        page,
        limit,
        searchFields,
      }),
    [sourceRows, search, conditions, sortField, sortOrder, page, limit, searchFields],
  );

  const pagination: ListPagination = {
    page,
    limit,
    total: result.total,
    totalPages: Math.ceil(result.total / limit),
  };

  const exportAll = useCallback(
    async (): Promise<T[]> =>
      applyClientQuery(sourceRows, {
        search,
        conditions,
        sortField,
        sortOrder,
        page: 1,
        limit: Math.max(sourceRows.length, 1),
        searchFields,
      }).rows,
    [sourceRows, search, conditions, sortField, sortOrder, searchFields],
  );

  const retry = useCallback(() => onRetry?.(), [onRetry]);

  return {
    source: 'local',
    capabilities,
    page,
    limit,
    search,
    conditions,
    sortField,
    sortOrder,
    params: { page, limit, sort, search: search || undefined, extraParams },
    setPage,
    setLimit,
    setSort,
    setSearch,
    setConditions,
    setFieldCondition,
    isFiltered,
    clearFilters,
    presets: EMPTY_PRESETS,
    activePresetId: null,
    togglePreset,
    rows: result.rows,
    pagination,
    status,
    error,
    retry,
    droppedFilters,
    mergeNotices,
    exportAll,
  };
}
